// Sends per week, with replies on hover.
//
// The first version drew sends as bars and replies as dots on a second, invisible scale, and it
// was unreadable: a dot floating above a bar means nothing without a scale, and giving it one
// would have needed a second axis for a series that is a twentieth the size of the first.
//
// The second printed the count on each bar and "N replies · x%" under each week. That read at
// three weeks and smeared at twenty-six. Now the dates and counts are thinned to every Nth bar
// from the room each bar actually has (trendLayout.ts), and each week's replies and rate are in
// its hover, with the window's totals in the key underneath.
//
// The SVG is drawn at its rendered pixel width rather than a fixed viewBox, so 10px text stays
// 10px on a phone instead of shrinking with the drawing -- which is what makes the label budget
// computable at all.
//
// No charting library, and no need for one: this is a rect and a label per week, drawn against
// CSS custom properties so a brand change reaches it like everything else.

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import type { ActivityWeek } from '../api/types';
import { formatCount } from '../lib/format';
import {
  foldWeeks, labelStride, monoTextWidth, showsLabel, valueLabelIndices, weekLabel, weekTooltip,
} from './trendLayout';

// Width before the first measurement; replaced as soon as the wrapper has a size.
const FALLBACK_WIDTH = 720;
const HEIGHT = 150;
const PADDING = { top: 22, right: 12, bottom: 24, left: 12 };

// Font sizes in px, matching .trend-label and .trend-value in activity.css.
const LABEL_FONT_PX = 10;
const VALUE_FONT_PX = 11;

// The wrapper's rendered width, tracked so the chart lays out in real pixels.
function useMeasuredWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    // A hidden or detached element reports zero; keep the last real width rather than drawing
    // into nothing.
    const observer = new ResizeObserver(entries => {
      const measured = entries[0]?.contentRect.width ?? 0;
      if (measured > 0) setWidth(Math.round(measured));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

export function Trend({
  weeks,
  minDenominator,
}: {
  weeks: ActivityWeek[];
  minDenominator: number;
}) {
  const points = useMemo(() => foldWeeks(weeks), [weeks]);
  const [chartRef, width] = useMeasuredWidth();

  if (!points.length) {
    return (
      <p className="covnote">
        No outbound activity in this period, so there is no trend to draw.
      </p>
    );
  }

  const plotWidth = width - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const maxSends = Math.max(...points.map(point => point.sends), 1);
  const slot = plotWidth / points.length;
  // A gap between bars, a ceiling so three weeks do not render as three slabs, and a floor so
  // a narrow screen still draws something visible.
  const barWidth = Math.max(3, Math.min(64, slot * 0.7, slot - 4));

  // How many bars each date and each count needs, from the widest one of each.
  const widestDate = Math.max(...points.map(point =>
    monoTextWidth(weekLabel(point.week), LABEL_FONT_PX)));
  const widestValue = Math.max(...points.map(point =>
    monoTextWidth(formatCount(point.sends), VALUE_FONT_PX)));
  const dateStride = labelStride(slot, widestDate);
  const valueStride = labelStride(slot, widestValue);
  const valueLabels = valueLabelIndices(points.map(point => point.sends), valueStride);

  // Each bar's top edge, so a count can clear a taller neighbour its text overhangs.
  const barTops = points.map(point =>
    PADDING.top + plotHeight - (point.sends / maxSends) * plotHeight);

  const totalSends = points.reduce((total, point) => total + point.sends, 0);
  const totalReplies = points.reduce((total, point) => total + point.replies, 0);

  return (
    <div className="trendwrap">
      <div ref={chartRef}>
        <svg
          viewBox={`0 0 ${width} ${HEIGHT}`}
          className="trend-chart"
          role="img"
          aria-label={
            'Outbound by week. '
            + points.map(point =>
              `Week of ${weekLabel(point.week)}: ${point.sends} sent, `
              + `${point.replies} replies, ${point.accounts} accounts worked`).join('. ')
          }
        >
          {/* The baseline, so a week with nothing in it reads as an empty week rather than as
              a gap in the drawing. */}
          <line
            x1={PADDING.left} y1={PADDING.top + plotHeight}
            x2={width - PADDING.right} y2={PADDING.top + plotHeight}
            className="trend-axis"
          />

          {points.map((point, index) => {
            const slotLeft = PADDING.left + slot * index;
            const centre = slotLeft + slot / 2;
            const barTop = barTops[index]!;
            const barHeight = PADDING.top + plotHeight - barTop;

            // The bars this week's count overhangs on either side, and the highest top among them.
            const valueWidth = monoTextWidth(formatCount(point.sends), VALUE_FONT_PX);
            const overhang = Math.max(0, Math.ceil((valueWidth / 2 + barWidth / 2) / slot) - 1);
            const nearbyTops = barTops.slice(Math.max(0, index - overhang), index + overhang + 1);
            const valueTop = Math.min(...nearbyTops);
            return (
              <g key={point.week} className="trend-week">
                {/* The native tooltip: the week's replies and rate live here now. */}
                <title>{weekTooltip(point, minDenominator)}</title>

                {/* The whole slot is the hover target, so a short bar is as easy to point at
                    as a tall one. */}
                <rect
                  x={slotLeft} y={0}
                  width={slot} height={HEIGHT}
                  className="trend-hit"
                />
                <rect
                  x={centre - barWidth / 2}
                  y={barTop}
                  width={barWidth}
                  height={Math.max(barHeight, point.sends ? 2 : 0)}
                  rx="2"
                  className="trend-bar"
                />

                {/* The count above its own bar: every bar when there is room, otherwise the
                    latest and the peak (valueLabelIndices). */}
                {valueLabels.has(index) ? (
                  <text x={centre} y={valueTop - 7} className="trend-value">
                    {formatCount(point.sends)}
                  </text>
                ) : null}

                {showsLabel(index, points.length, dateStride) ? (
                  <text x={centre} y={HEIGHT - 8} className="trend-label">
                    {weekLabel(point.week)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>

      <p className="trendkey">
        <span className="keybar" /> outbound touches per week, by everyone in scope
        <span className="keyhint">(hover a bar for its replies)</span>
        <span className="keynote">
          {formatCount(totalSends)} sent · {formatCount(totalReplies)} replied
          {' '}across {points.length} week{points.length === 1 ? '' : 's'}
        </span>
      </p>
    </div>
  );
}
